import { useState, useMemo } from 'react';
import {
  Stack,
  Paper,
  Text,
  Group,
  Badge,
  TextInput,
  Skeleton,
  Card,
  SimpleGrid,
  Button,
  NumberInput,
} from '@mantine/core';
import {
  IconSearch,
  IconShoppingCartPlus,
  IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import { useRecentOrderItems, useAddToCart } from '@/hooks/usePortalApi';
import type { RecentOrderItem } from '@/hooks/usePortalApi';
import dayjs from 'dayjs';

export default function QuickReorder() {
  const [search, setSearch] = useState('');
  const [quantities, setQuantities] = useState<Record<number, number>>({});

  const { data, isLoading } = useRecentOrderItems(50);
  const addToCartMutation = useAddToCart();

  const items: RecentOrderItem[] = data?.items ?? [];

  const filtered = useMemo(() => {
    let result = [...items];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.product_name.toLowerCase().includes(s) ||
          r.sku.toLowerCase().includes(s),
      );
    }
    // Sort by most recently ordered first
    result.sort((a, b) => {
      const dateA = new Date(a.last_ordered_date).getTime();
      const dateB = new Date(b.last_ordered_date).getTime();
      return dateB - dateA;
    });
    return result;
  }, [items, search]);

  const getQuantity = (item: RecentOrderItem) => {
    return quantities[item.product_id] ?? item.last_quantity;
  };

  const setQuantity = (productId: number, qty: number) => {
    setQuantities((prev) => ({ ...prev, [productId]: Math.max(1, qty) }));
  };

  const handleAddToCart = async (item: RecentOrderItem) => {
    const qty = getQuantity(item);
    if (qty <= 0) {
      notifications.show({
        title: 'Invalid Quantity',
        message: 'Please enter a quantity greater than 0',
        color: 'orange',
      });
      return;
    }

    try {
      await addToCartMutation.mutateAsync({
        product_id: item.product_id,
        quantity: qty,
      });
      notifications.show({
        title: 'Added to Cart',
        message: `${qty}x ${item.product_name} added to your cart`,
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to add item to cart. Please try again.',
        color: 'red',
      });
    }
  };

  const handleAddAllToCart = async () => {
    if (filtered.length === 0) return;

    try {
      let count = 0;
      for (const item of filtered) {
        const qty = getQuantity(item);
        if (qty > 0) {
          await addToCartMutation.mutateAsync({
            product_id: item.product_id,
            quantity: qty,
          });
          count++;
        }
      }
      if (count > 0) {
        notifications.show({
          title: 'Added to Cart',
          message: `${count} item${count > 1 ? 's' : ''} added to your cart`,
          color: 'green',
        });
      }
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to add items to cart. Please try again.',
        color: 'red',
      });
    }
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="Quick Reorder"
        description="One-click reorder from your previous orders"
        actions={
          filtered.length > 0 ? (
            <Button
              leftSection={<IconShoppingCartPlus size={18} />}
              onClick={handleAddAllToCart}
              loading={addToCartMutation.isPending}
            >
              Add All to Cart
            </Button>
          ) : undefined
        }
      />

      <TextInput
        placeholder="Search by product name or SKU..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      {isLoading ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} height={220} radius="md" />
          ))}
        </SimpleGrid>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<IconRefresh size={30} />}
          title="No recent order items"
          description={
            search
              ? 'No items match your search criteria'
              : 'Place an order first to see items available for quick reorder'
          }
        />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {filtered.map((item) => (
            <Card key={item.id} shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="sm">
                <div>
                  <Text size="md" fw={600} lineClamp={2}>
                    {item.product_name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    SKU: {item.sku}
                  </Text>
                </div>

                <Group justify="space-between">
                  <Text size="xl" fw={700}>
                    ${parseFloat(item.unit_price).toFixed(2)}
                  </Text>
                  <Badge variant="light" color="blue" size="sm">
                    {item.order_count} order{item.order_count !== 1 ? 's' : ''}
                  </Badge>
                </Group>

                <Paper p="xs" bg="gray.0" radius="sm">
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">
                      Last ordered
                    </Text>
                    <Text size="xs" fw={500}>
                      {dayjs(item.last_ordered_date).format('MMM D, YYYY')}
                    </Text>
                  </Group>
                  <Group justify="space-between" mt={4}>
                    <Text size="xs" c="dimmed">
                      Last quantity
                    </Text>
                    <Text size="xs" fw={500}>
                      {item.last_quantity}
                    </Text>
                  </Group>
                </Paper>

                <Group gap="sm" align="flex-end">
                  <NumberInput
                    label="Qty"
                    value={getQuantity(item)}
                    onChange={(value) =>
                      setQuantity(item.product_id, Number(value) || 1)
                    }
                    min={1}
                    max={9999}
                    style={{ flex: 1 }}
                    size="sm"
                  />
                  <Button
                    leftSection={<IconShoppingCartPlus size={16} />}
                    size="sm"
                    onClick={() => handleAddToCart(item)}
                    loading={addToCartMutation.isPending}
                  >
                    Add to Cart
                  </Button>
                </Group>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}
